import { Grid, Box } from '@material-ui/core';
import MuiAutocomplete from '@mui/material/Autocomplete';
import MuiButton from '@mui/material/Button';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiTextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import { ITask } from '../../../../models/task.model';
import { ITaskItem } from '../../../../models/taskItem.model';
import { Block } from '../../../../state/block';
import { Customer } from '../../../../state/customer';
import {
  CompanyComboBox,
  SalesTeamMemberComboBox,
  BlockEditDialog,
} from '../../../components';
import CustomCaption from '../../../components/CustomCaption';
import useStyles from './sampleDevelopmentNl.style';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTaskItemList } from '../../../../state/taskItem';
import { Company } from '../../../../state/company';
import { TaskItemState } from '../../../../state/taskItem';
import { AppState } from '../../../../state/configureStore';
import { SalesTeamMember } from '../../../../state/salesTeamMember';
import { setBlock, BlockState } from '../../../../state/block';
import { CRTFieldValueState } from '../../../../state/crt';
import { fetchCustomerList as fetchCustomerListService } from '../../../../services/customerService';
import { fetchTaskList as fetchTaskListService } from '../../../../services/taskService';
import { fetchTaskItemList as fetchTaskItemListService } from '../../../../services/taskItemService';
import { JobTypeOptions as jobTypeOptions } from '../../../../constants/jobType';
import { UnitOfQuantityOptions as unitOfQuantityOptions } from '../../../../constants/unitOfQuantity';

interface SampleDevelopmentNlHeadProps {
  values: any;
  handleChange: any;
  errors: any;
  setFieldValue: any;
}

const SampleDevelopmentNlHead: React.FC<SampleDevelopmentNlHeadProps> = (props) => {
  const { values, handleChange, errors, setFieldValue } = props;
  const dispatch = useDispatch();
  const classes = useStyles();

  const { items: taskItems } = useSelector<AppState, TaskItemState>((t) => t.taskItem);
  const { item: block } = useSelector<AppState, BlockState>((t) => t.block);
  const [customerOptions, setCustomerOptions] = useState<Customer[]>([]);
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);
  const [taskOptions, setTaskOptions] = useState<ITask[]>([]);
  const [taskInputValue, setTaskInputValue] = useState('');
  const [taskLoading, setTaskLoading] = useState(false);
  const [blockOptions, setBlockOptions] = useState<Block[]>([]);
  const [blockInputValue, setBlockInputValue] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);

  //get customer requirement from state
  const { items: customerRequirements } = useSelector<AppState, CRTFieldValueState>(
    (t) => t.customerRequirement,
  );

  useEffect(() => {
    if (customerRequirements.length > 0) {
      //filter customer requirements by field value must value not null or empty;
      const filterCrt = customerRequirements.filter(
        (f) => f.value !== null && f.value !== '' && !f.fieldId.startsWith('cmk'),
      );

      //loop through customer requirements and set the field value
      filterCrt.forEach((crt) => {
        //set value
        setFieldValue(crt.fieldId, crt.value);
      });
    }
  }, [customerRequirements]);

  const [openBlockEditDialog, setOpenBlockEditDialog] = useState(false);

  useEffect(() => {
    let active = true;

    setCustomerLoading(true);
    fetchCustomerListService({
      filters: { 'DisplayName.Contains': customerInputValue },
      paging: { pageNumber: 0, pageSize: 10 },
    })
      .then(({ data: rows }) => {
        if (active) {
          setCustomerOptions(rows);
          setCustomerLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setCustomerOptions([]);
          setCustomerLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [customerInputValue]);

  const selectedTask = (values.smTask as ITask | null) ?? null;
  const selectedTaskId = selectedTask?.id ?? values.smTaskId;

  useEffect(() => {
    let active = true;

    setTaskLoading(true);
    fetchTaskListService({
      filters: { 'TaskNumber.Contains': taskInputValue },
      paging: { pageNumber: 0, pageSize: 10 },
    })
      .then(({ data: rows }) => {
        if (active) {
          setTaskOptions(rows);
          setTaskLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setTaskOptions([]);
          setTaskLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [taskInputValue]);

  useEffect(() => {
    let active = true;

    if (!selectedTaskId) {
      setBlockOptions([]);
      setBlockLoading(false);
      return () => {
        active = false;
      };
    }

    setBlockLoading(true);
    fetchTaskItemListService({
      filters: {
        'SMTaskId.EqualsTo': selectedTaskId.toString(),
        'OpportunityItem.Block.BlockNo.StartsWith': blockInputValue,
        'IsUseCRTNumber.EqualsTo': 'true',
      },
      paging: { pageNumber: 0, pageSize: 10 },
    })
      .then(({ data: taskItemRows }) => {
        if (active) {
          const rows = taskItemRows.map(
            (taskItem: ITaskItem) => taskItem.opportunityItem!.block,
          );
          setBlockOptions(rows);
          setBlockLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setBlockOptions([]);
          setBlockLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [blockInputValue, selectedTaskId]);

  useEffect(() => {
    if (taskItems.length > 0) {
      setFieldValue('salesTeam', taskItems[0].opportunityItem!.opportunity!.salesTeam);
      setFieldValue('orderCompany', taskItems[0].opportunityItem!.opportunity!.company);
      setFieldValue(
        'orderCompanyId',
        taskItems[0].opportunityItem!.opportunity!.company!.id!,
      );

      setFieldValue('crtNumber', taskItems[0].crtNumber);
    }
  }, [taskItems, setFieldValue]);

  useEffect(() => {
    if (
      block &&
      block.itemDescription &&
      block.itemDescription !== values.block.itemDescription
    ) {
      setFieldValue('block', block);
    }
  }, [block, values.block, setFieldValue]);

  const selectedCustomer = (values.customer as Customer | null) ?? null;
  const selectedBlock = (values.block as Block | null) ?? null;
  const getCustomerOptionLabel = (customer: Customer) =>
    customer.displayName || `${customer.externalId} ${customer.name}`.trim();
  const getTaskOptionLabel = (task: ITask) => task.taskNumber ?? '';
  const getBlockOptionLabel = (option: Block) => option.blockNo ?? '';
  const customerAutocompleteOptions =
    selectedCustomer &&
    !customerOptions.some((option) => option.id === selectedCustomer.id)
      ? [selectedCustomer, ...customerOptions]
      : customerOptions;
  const taskAutocompleteOptions =
    selectedTask && !taskOptions.some((option) => option.id === selectedTask.id)
      ? [selectedTask, ...taskOptions]
      : taskOptions;
  const blockAutocompleteOptions =
    selectedBlock && !blockOptions.some((option) => option.id === selectedBlock.id)
      ? [selectedBlock, ...blockOptions]
      : blockOptions;
  const renderFieldTitle = (label: string) => <CustomCaption caption={label} />;

  return (
    <>
      <BlockEditDialog
        open={openBlockEditDialog}
        onClose={() => {
          setOpenBlockEditDialog(false);
        }}
      />
      <Grid
        container
        spacing={1}
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Grid container item xs={12} spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            {renderFieldTitle('Customer')}
            <MuiAutocomplete
              size="small"
              options={customerAutocompleteOptions}
              value={selectedCustomer}
              loading={customerLoading}
              filterOptions={(options) => options}
              getOptionLabel={(option) => getCustomerOptionLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(event, inputValue) => {
                setCustomerInputValue(inputValue);
              }}
              onChange={(event, result) => {
                setFieldValue('customer', result ?? null);
                setFieldValue('customerId', result ? result.id : null);
              }}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  size="small"
                  placeholder="Type Customer Name"
                  variant="outlined"
                  margin="dense"
                  error={!!errors.customer}
                  helperText={errors.customer}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            {renderFieldTitle('Task')}
            <MuiAutocomplete
              size="small"
              options={taskAutocompleteOptions}
              value={selectedTask}
              loading={taskLoading}
              filterOptions={(options) => options}
              getOptionLabel={(option) => getTaskOptionLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(event, inputValue) => {
                setTaskInputValue(inputValue);
              }}
              onChange={(event, result) => {
                if (!result) {
                  setFieldValue('block', null);
                  setFieldValue('blockId', null);
                  setFieldValue('salesTeamMember', null);
                  setFieldValue('salesTeamMemberId', null);
                  setFieldValue('salesTeam', null);
                  setFieldValue('orderCompany', null);
                  setFieldValue('orderCompanyId', null);
                }
                setFieldValue('smTask', result ?? null);
                setFieldValue('smTaskId', result ? result.id : null);
              }}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  size="small"
                  placeholder="Type Task Number"
                  variant="outlined"
                  margin="dense"
                  error={!!errors.smTask}
                  helperText={errors.smTask}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            {renderFieldTitle('Block No')}
            <MuiAutocomplete
              size="small"
              options={blockAutocompleteOptions}
              value={selectedBlock}
              loading={blockLoading}
              disabled={!values.smTask}
              filterOptions={(options) => options}
              getOptionLabel={(option) => getBlockOptionLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(event, inputValue) => {
                setBlockInputValue(inputValue);
              }}
              onChange={(event, result) => {
                if (!result) {
                  setFieldValue('salesTeamMember', null);
                  setFieldValue('salesTeamMemberId', null);
                  setFieldValue('salesTeam', null);
                  setFieldValue('orderCompany', null);
                  setFieldValue('orderCompanyId', null);
                }
                setFieldValue('block', result ?? null);
                setFieldValue('blockId', result ? result.id : null);
                if (result) {
                  dispatch(
                    fetchTaskItemList(
                      {
                        'OpportunityItem.BlockId.EqualsTo': result.id!.toString(),
                        'SMTaskId.EqualsTo': values.smTask.id,
                        'IsUseCRTNumber.EqualsTo': 'true',
                      },
                      { pageNumber: 0, pageSize: 10 },
                    ),
                  );
                }
              }}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  size="small"
                  placeholder="Type Block Name"
                  variant="outlined"
                  margin="dense"
                  error={!!errors.block}
                  helperText={errors.block}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            {renderFieldTitle('Customer Requirement')}
            <MuiTextField
              name="type"
              type="input"
              placeholder="Customer Requirement"
              size="small"
              variant="outlined"
              margin="dense"
              value={values.crtNumber ?? ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={2}>
            {renderFieldTitle('Type')}
            <MuiTextField
              name="type"
              type="input"
              placeholder="Type"
              size="small"
              variant="outlined"
              margin="dense"
              value={values.block ? values.block.productType?.description : ''}
              fullWidth
              disabled
            />
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={1} alignItems="center">
          <Grid item xs={12} md={3}>
            {renderFieldTitle('Job Type')}
            <MuiTextField
              name="jobType"
              select
              placeholder="Job Type"
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
              value={values.jobType ?? ''}
              onChange={handleChange}
              error={!!errors.jobType}
              helperText={errors.jobType}
              SelectProps={{ displayEmpty: true }}
            >
              {jobTypeOptions.map((jobType) => (
                <MuiMenuItem key={jobType.value} value={jobType.value}>
                  {jobType.label}
                </MuiMenuItem>
              ))}
            </MuiTextField>
          </Grid>
          <Grid item xs={12} md={3}>
            {renderFieldTitle('See Model')}
            <MuiTextField
              name="seeModel"
              type="date"
              size="small"
              variant="outlined"
              margin="dense"
              value={values.seeModel ?? ''}
              onChange={handleChange}
              error={!!errors.seeModel}
              helperText={errors.seeModel}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            {renderFieldTitle('Due Date')}
            <MuiTextField
              name="dueDate"
              type="date"
              size="small"
              variant="outlined"
              margin="dense"
              value={values.dueDate ?? ''}
              onChange={handleChange}
              error={!!errors.dueDate}
              helperText={errors.dueDate}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            {renderFieldTitle('Quantity')}
            <MuiTextField
              name="quantity"
              type="number"
              placeholder="Quantity"
              size="small"
              variant="outlined"
              margin="dense"
              value={values.quantity ?? ''}
              onChange={handleChange}
              error={!!errors.quantity}
              helperText={errors.quantity}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            {renderFieldTitle('Unit')}
            <MuiTextField
              name="quantityUnit"
              select
              placeholder="Unit"
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
              value={values.quantityUnit ?? ''}
              onChange={handleChange}
              error={!!errors.quantityUnit}
              helperText={errors.quantityUnit}
              SelectProps={{ displayEmpty: true }}
            >
              {unitOfQuantityOptions.map((unitOfQuantity) => (
                <MuiMenuItem key={unitOfQuantity.value} value={unitOfQuantity.value}>
                  {unitOfQuantity.label}
                </MuiMenuItem>
              ))}
            </MuiTextField>
          </Grid>
        </Grid>

        {/* <Grid container item xs={12} spacing={1} alignItems="center"> */}
        {/* <Grid item xs={12} md={3}>
            <TextField
              className={classes.textInput}
              name="salesTeamName"
              type="input"
              label="SalesTeam Name"
              variant="outlined"
              margin="dense"
              InputLabelProps={{ shrink: true }}
              value={values.salesTeam ? values.salesTeam.name : ''}
              fullWidth
              disabled
            />
          </Grid> */}
        {/* <Grid item xs={12} md={3}>
            <SalesTeamMemberComboBox
              handleChange={(result) => {
                setFieldValue('salesTeamMember', result ? result : null);
                setFieldValue('salesTeamMemberId', result ? result.id : null);
              }}
              selectedValue={values.salesTeamMember as SalesTeamMember}
              error={!!errors.salesTeamMember}
              helperText={errors.salesTeamMember}
              isDisabled={!values.smTask || !values.block}
              salesTeamId={values.salesTeam ? values.salesTeam.id : ''}
            />
          </Grid> */}
        {/* <Grid item xs={12} md={3}>
            <CompanyComboBox
              handleChange={() => {}}
              selectedValue={values.orderCompany as Company}
              isDisabled
            />
          </Grid> */}

        {/* <Grid item xs={12} md={3} /> */}
        {/* </Grid> */}
        <Grid
          container
          spacing={1}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          item
          xs={12}
        >
          <Grid item xs={12} md={2}>
            {renderFieldTitle('Program')}
            <MuiTextField
              name="program"
              type="input"
              placeholder="Program"
              size="small"
              variant="outlined"
              margin="dense"
              value={values.block ? values.block.program?.name : ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={2}>
            {renderFieldTitle('Brand')}
            <MuiTextField
              name="brand"
              type="input"
              placeholder="Brand"
              size="small"
              variant="outlined"
              margin="dense"
              value={values.block ? values.block.brand?.name : ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={3}>
            {renderFieldTitle('Item')}
            <MuiTextField
              name="item"
              type="input"
              placeholder="Item"
              size="small"
              variant="outlined"
              margin="dense"
              value={values.block ? values.block.item : ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            {renderFieldTitle('Description')}
            <div className={classes.blockDescriptionContainer}>
              <MuiTextField
                name="description"
                type="input"
                placeholder="Description"
                size="small"
                variant="outlined"
                margin="dense"
                value={values.block ? values.block.itemDescription : ''}
                disabled
                fullWidth
              />
            </div>
          </Grid>
          <Grid item xs={12} md={1}>
            <Box mt={2}>
              <MuiButton
                fullWidth
                color="inherit"
                variant="contained"
                onClick={() => {
                  if (values.block) {
                    dispatch(setBlock(values.block));
                    setOpenBlockEditDialog(true);
                  }
                }}
              >
                EDIT
              </MuiButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SampleDevelopmentNlHead;
